/**
 * Claim Detail Page
 * Shows full claim information with timeline, watchers, and actions
 */

import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  getClaimDetail,
  updateClaimStatus,
  settleClaim,
  formatCurrency,
  getStatusColor,
  getStatusLabel,
  getSeverityColor,
  getClaimTypeLabel,
  completeStage
} from '../api/claims';
import VolumeChart from '../components/claims/VolumeChart';
import WorkflowManager from '../components/claims/WorkflowManager';
import FinancialMetrics from '../components/claims/FinancialMetrics';
import ProbabilityBadge from '../components/claims/ProbabilityBadge';
import LinkOpportunityModal from '../components/claims/LinkOpportunityModal';

const ClaimDetail = () => {
  const { claimId } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [showSettleModal, setShowSettleModal] = useState(false);
  const [showLinkOpportunityModal, setShowLinkOpportunityModal] = useState(false);
  const [newStatus, setNewStatus] = useState('');
  const [statusNotes, setStatusNotes] = useState('');
  const [settlementAmount, setSettlementAmount] = useState('');
  const [settlementDate, setSettlementDate] = useState('');
  const [settlementNotes, setSettlementNotes] = useState('');

  // Fetch claim detail
  const { data, isLoading, error } = useQuery({
    queryKey: ['claim', claimId],
    queryFn: () => getClaimDetail(claimId)
  });

  // Fetch workflow stages status to determine current stage
  const { data: stagesStatusData } = useQuery({
    queryKey: ['allStagesStatus', claimId],
    queryFn: async () => {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL || ''}/api/v1/claims/${claimId}/stages/all`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      return response.json();
    },
    enabled: !!claimId
  });

  // Update status mutation
  const statusMutation = useMutation({
    mutationFn: (statusData) => updateClaimStatus(claimId, statusData),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['claim', claimId] });
      setShowStatusModal(false);
      setNewStatus('');
      setStatusNotes('');
    }
  });

  // Settle claim mutation
  const settleMutation = useMutation({
    mutationFn: (settlementData) => settleClaim(claimId, settlementData),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['claim', claimId] });
      setShowSettleModal(false);
    }
  });

  const handleStatusUpdate = () => {
    if (!newStatus) return;
    
    statusMutation.mutate({
      status: newStatus,
      notes: statusNotes
    });
  };

  const handleSettle = () => {
    if (!settlementAmount || !settlementDate) return;
    
    settleMutation.mutate({
      settlement_amount_cents: parseInt(parseFloat(settlementAmount) * 100),
      settlement_date: settlementDate,
      settlement_notes: settlementNotes
    });
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (error || !data?.claim) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <p className="text-red-600 mb-4">Error loading claim</p>
          <button
            onClick={() => navigate('/claims')}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg"
          >
            Back to Claims
          </button>
        </div>
      </div>
    );
  }

  const claim = data.claim;
  const stagesStatus = stagesStatusData?.data || [];

  // Determine current workflow stage based on completion status
  const WORKFLOW_STAGES = [
    { key: 'claim_definition', label: 'Claim Definition' },
    { key: 'team_definition', label: 'Team Definition' },
    { key: 'customer_contact', label: 'Customer Contact' },
    { key: 'calculation', label: 'Calculation' },
    { key: 'cost_price_profit', label: 'Cost | Price | Profit' },
    { key: 'quote_negotiation', label: 'Quote & Negotiation' },
    { key: 'settlement_invoicing', label: 'Settlement & Invoicing' },
    { key: 'closed', label: 'Closed' }
  ];

  const getCurrentWorkflowStage = () => {
    if (!stagesStatus.length) return null;
    
    // Find first incomplete stage
    for (let stage of WORKFLOW_STAGES) {
      const status = stagesStatus.find(s => s.stage_name === stage.key);
      if (!status?.is_completed) {
        return stage.label;
      }
    }
    // If all completed, return last stage
    return WORKFLOW_STAGES[WORKFLOW_STAGES.length - 1].label;
  };

  const currentWorkflowStage = getCurrentWorkflowStage();

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center justify-between mb-4">
            <button
              onClick={() => navigate('/claims')}
              className="text-gray-600 hover:text-gray-900 flex items-center"
            >
              <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
              Back to Claims
            </button>
          </div>

          <div className="flex items-start justify-between">
            <div className="flex-1">
              <div className="flex items-center space-x-4">
                <h1 className="text-3xl font-bold text-gray-900">{claim.claim_number}</h1>
                <span
                  className="px-3 py-1 text-sm font-semibold rounded-full bg-blue-100 text-blue-800"
                >
                  {currentWorkflowStage || getStatusLabel(claim.status)}
                </span>
                <span
                  className={`px-3 py-1 text-xs font-medium rounded-full
                    ${getSeverityColor(claim.severity) === 'red' ? 'bg-red-100 text-red-800' :
                      getSeverityColor(claim.severity) === 'orange' ? 'bg-orange-100 text-orange-800' :
                      getSeverityColor(claim.severity) === 'yellow' ? 'bg-yellow-100 text-yellow-800' :
                      'bg-green-100 text-green-800'
                    }`}
                >
                  {claim.severity.toUpperCase()}
                </span>
                {/* Probability Badge - Phase 1 */}
                {claim.probability && <ProbabilityBadge probability={claim.probability} />}
              </div>
              <p className="mt-2 text-gray-600">
                {getClaimTypeLabel(claim.claim_type)} • {claim.time_period}
              </p>
            </div>

            <div className="flex space-x-3">
              <button
                onClick={() => setShowStatusModal(true)}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                Update Status
              </button>
              {claim.status === 'approved' && (
                <button
                  onClick={() => setShowSettleModal(true)}
                  className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                >
                  Mark as Settled
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left Column - Main Info */}
          <div className="lg:col-span-2 space-y-6">
            {/* Workflow Manager - Phase 1 Enhanced */}
            <WorkflowManager 
              claimId={claimId} 
              currentStage={claim.status}
              onStageChange={async (newStage, notes) => {
                try {
                  await completeStage(claimId, newStage, notes);
                  // Invalidate queries to refresh data
                  queryClient.invalidateQueries(['claim', claimId]);
                  queryClient.invalidateQueries(['claims']);
                } catch (error) {
                  console.error('Failed to change stage:', error);
                  alert('Failed to update stage. Please try again.');
                }
              }}
            />

            {/* Volume Chart - Phase 1 */}
            <VolumeChart claimId={claimId} />

            {/* Financial Metrics - Phase 1 */}
            <FinancialMetrics claimId={claimId} />

            {/* Original Financial Impact Card */}
            <div className="bg-white rounded-lg shadow p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Financial Impact</h2>
              <div className="text-4xl font-bold text-red-600 mb-2">
                {formatCurrency(claim.financial_impact_cents)}
              </div>
              {claim.settlement_amount_cents && (
                <div className="text-lg text-green-600 mt-2">
                  Settled: {formatCurrency(claim.settlement_amount_cents)}
                </div>
              )}
            </div>

            {/* Volume Details */}
            {claim.contracted_volume && (
              <div className="bg-white rounded-lg shadow p-6">
                <h2 className="text-lg font-semibold text-gray-900 mb-4">Volume Details</h2>
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <div className="text-sm text-gray-600">Contracted Volume</div>
                    <div className="text-2xl font-semibold text-gray-900 mt-1">
                      {claim.contracted_volume?.toLocaleString()}
                    </div>
                  </div>
                  <div>
                    <div className="text-sm text-gray-600">Actual Volume</div>
                    <div className="text-2xl font-semibold text-gray-900 mt-1">
                      {claim.actual_volume?.toLocaleString()}
                    </div>
                  </div>
                  <div>
                    <div className="text-sm text-gray-600">Shortage</div>
                      <div className="text-2xl font-semibold text-red-600 mt-1">
                        {claim.volume_deviation?.toLocaleString()}
                      </div>
                      {claim.volume_deviation_pct && (
                        <div className="text-sm text-gray-600 mt-1">
                         ({Number(claim.volume_deviation_pct).toFixed(1)}% short)
                        </div>
                      )}
                    </div>
                </div>
              </div>
            )}

            {/* Pursuit & Part Info */}
            <div className="bg-white rounded-lg shadow p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-semibold text-gray-900">Related Information</h2>
                <button
                  onClick={() => setShowLinkOpportunityModal(true)}
                  className="text-sm text-blue-600 hover:text-blue-800 font-medium flex items-center gap-1"
                >
                  <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
                  </svg>
                  {claim.opportunity ? 'Change' : 'Link'} Pursuit
                </button>
              </div>
              <div className="space-y-4">
                {claim.opportunity ? (
                  <div>
                    <div className="text-sm font-medium text-gray-600">Pursuit</div>
                    <div className="mt-1">
                      <button
                        onClick={() => navigate('/pipeline')}
                        className="text-blue-600 hover:text-blue-800 font-medium"
                        title="View in Pipeline Board"
                      >
                        {claim.opportunity.program_name}
                      </button>
                      <span className="text-gray-600 ml-2">• {claim.opportunity.oem}</span>
                    </div>
                  </div>
                ) : (
                  <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                    <p className="text-sm text-yellow-800">
                      No pursuit linked. Click "Link Pursuit" above to connect this claim for automatic data population.
                    </p>
                  </div>
                )}
                {claim.part && (
                  <div>
                    <div className="text-sm font-medium text-gray-600">Part</div>
                    <div className="mt-1 text-gray-900">
                      {claim.part.part_number} - {claim.part.description}
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Notes */}
            {(claim.notes || claim.negotiation_notes) && (
              <div className="bg-white rounded-lg shadow p-6">
                <h2 className="text-lg font-semibold text-gray-900 mb-4">Notes</h2>
                {claim.notes && (
                  <div className="mb-4">
                    <div className="text-sm font-medium text-gray-600 mb-2">Internal Notes</div>
                    <div className="text-gray-900 whitespace-pre-wrap">{claim.notes}</div>
                  </div>
                )}
                {claim.negotiation_notes && (
                  <div>
                    <div className="text-sm font-medium text-gray-600 mb-2">Negotiation Notes</div>
                    <div className="text-gray-900 whitespace-pre-wrap">{claim.negotiation_notes}</div>
                  </div>
                )}
              </div>
            )}

            {/* Activity Timeline */}
            <div className="bg-white rounded-lg shadow p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Activity Timeline</h2>
              <div className="space-y-4">
                {claim.activities && claim.activities.length > 0 ? (
                  claim.activities.map((activity) => (
                    <div key={activity.id} className="flex space-x-3">
                      <div className="flex-shrink-0">
                        <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                          <svg className="w-4 h-4 text-blue-600" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clipRule="evenodd" />
                          </svg>
                        </div>
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-900">{activity.description}</p>
                        <div className="mt-1 flex items-center space-x-2 text-xs text-gray-500">
                          <span>{activity.created_by.name}</span>
                          <span>•</span>
                          <span>{formatDateTime(activity.created_at)}</span>
                        </div>
                      </div>
                    </div>
                  ))
                ) : (
                  <p className="text-gray-500 text-sm">No activity yet</p>
                )}
              </div>
            </div>
          </div>

          {/* Right Column - Sidebar */}
          <div className="space-y-6">
            {/* Responsible User */}
            <div className="bg-white rounded-lg shadow p-6">
              <h3 className="text-sm font-semibold text-gray-900 mb-3">Responsible User</h3>
              {claim.responsible_user ? (
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                    <span className="text-blue-600 font-semibold">
                      {claim.responsible_user.name.charAt(0).toUpperCase()}
                    </span>
                  </div>
                  <div>
                    <div className="text-sm font-medium text-gray-900">
                      {claim.responsible_user.name}
                    </div>
                    <div className="text-xs text-gray-500">
                      {claim.responsible_user.email}
                    </div>
                  </div>
                </div>
              ) : (
                <p className="text-gray-500 text-sm">Unassigned</p>
              )}
            </div>

            {/* Watchers */}
            <div className="bg-white rounded-lg shadow p-6">
              <h3 className="text-sm font-semibold text-gray-900 mb-3">
                Watchers ({claim.watchers?.length || 0})
              </h3>
              <div className="space-y-3">
                {claim.watchers && claim.watchers.length > 0 ? (
                  claim.watchers.map((watcher) => (
                    <div key={watcher.id} className="flex items-center space-x-2">
                      <div className="w-8 h-8 bg-gray-100 rounded-full flex items-center justify-center">
                        <span className="text-gray-600 text-xs font-semibold">
                          {watcher.name.charAt(0).toUpperCase()}
                        </span>
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="text-sm text-gray-900 truncate">{watcher.name}</div>
                      </div>
                    </div>
                  ))
                ) : (
                  <p className="text-gray-500 text-sm">No watchers</p>
                )}
              </div>
            </div>

            {/* Key Dates */}
            <div className="bg-white rounded-lg shadow p-6">
              <h3 className="text-sm font-semibold text-gray-900 mb-3">Key Dates</h3>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-600">Created:</span>
                  <span className="text-gray-900 font-medium">{formatDate(claim.created_at)}</span>
                </div>
                {claim.submitted_at && (
                  <div className="flex justify-between">
                    <span className="text-gray-600">Submitted:</span>
                    <span className="text-gray-900 font-medium">{formatDate(claim.submitted_at)}</span>
                  </div>
                )}
                {claim.approved_at && (
                  <div className="flex justify-between">
                    <span className="text-gray-600">Approved:</span>
                    <span className="text-gray-900 font-medium">{formatDate(claim.approved_at)}</span>
                  </div>
                )}
                {claim.settled_at && (
                  <div className="flex justify-between">
                    <span className="text-gray-600">Settled:</span>
                    <span className="text-gray-900 font-medium">{formatDate(claim.settled_at)}</span>
                  </div>
                )}
              </div>
            </div>

            {/* Settlement Info */}
            {claim.settlement_amount_cents && (
              <div className="bg-green-50 border border-green-200 rounded-lg p-6">
                <h3 className="text-sm font-semibold text-green-900 mb-3">Settlement Details</h3>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-green-700">Amount:</span>
                    <span className="text-green-900 font-bold">
                      {formatCurrency(claim.settlement_amount_cents)}
                    </span>
                  </div>
                  {claim.settlement_date && (
                    <div className="flex justify-between">
                      <span className="text-green-700">Date:</span>
                      <span className="text-green-900 font-medium">
                        {formatDate(claim.settlement_date)}
                      </span>
                    </div>
                  )}
                  {claim.settlement_notes && (
                    <div className="mt-3 pt-3 border-t border-green-200">
                      <div className="text-green-700 mb-1">Notes:</div>
                      <div className="text-green-900">{claim.settlement_notes}</div>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Status Update Modal */}
      {showStatusModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Update Claim Status</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  New Status
                </label>
                <select
                  value={newStatus}
                  onChange={(e) => setNewStatus(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">Select status...</option>
                  <option value="draft">Draft</option>
                  <option value="submitted">Submitted</option>
                  <option value="under_review">Under Review</option>
                  <option value="approved">Approved</option>
                  <option value="in_negotiation">In Negotiation</option>
                  <option value="rejected">Rejected</option>
                  <option value="closed">Closed</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Notes (optional)
                </label>
                <textarea
                  value={statusNotes}
                  onChange={(e) => setStatusNotes(e.target.value)}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  placeholder="Add any notes about this status change..."
                />
              </div>
              <div className="flex space-x-3">
                <button
                  onClick={handleStatusUpdate}
                  disabled={!newStatus || statusMutation.isLoading}
                  className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-400"
                >
                  {statusMutation.isLoading ? 'Updating...' : 'Update Status'}
                </button>
                <button
                  onClick={() => setShowStatusModal(false)}
                  className="flex-1 px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Settle Claim Modal */}
      {showSettleModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Settle Claim</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Settlement Amount ($)
                </label>
                <input
                  type="number"
                  value={settlementAmount}
                  onChange={(e) => setSettlementAmount(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  placeholder="150000"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Settlement Date
                </label>
                <input
                  type="date"
                  value={settlementDate}
                  onChange={(e) => setSettlementDate(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Notes (optional)
                </label>
                <textarea
                  value={settlementNotes}
                  onChange={(e) => setSettlementNotes(e.target.value)}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  placeholder="Add any notes about the settlement..."
                />
              </div>
              <div className="flex space-x-3">
                <button
                  onClick={handleSettle}
                  disabled={!settlementAmount || !settlementDate || settleMutation.isLoading}
                  className="flex-1 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:bg-gray-400"
                >
                  {settleMutation.isLoading ? 'Settling...' : 'Mark as Settled'}
                </button>
                <button
                  onClick={() => setShowSettleModal(false)}
                  className="flex-1 px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Link Pursuit Modal */}
      <LinkOpportunityModal
        open={showLinkOpportunityModal}
        onOpenChange={setShowLinkOpportunityModal}
        claimId={claimId}
        currentOpportunityId={claim?.opportunity?.id}
        onSuccess={() => {
          // Refresh claim data after linking
          queryClient.invalidateQueries(['claimDetail', claimId]);
        }}
      />
    </div>
  );
};

export default ClaimDetail;
